import { useEffect, useRef, useState } from 'react';
import type { Thread, SenderIdentity, ContactSummary } from '../../lib/api';
import { clientAccountsApi } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import { useChatStore } from '../../stores/chat';
import { apiErrorMessage } from '../../lib/apiError';
import { typeLabel, badgeStyle } from '../../lib/threadLabels';
import { participantRole } from '../../lib/roleStyles';
import Sheet from '../ui/Sheet';
import SegmentedControl from '../ui/SegmentedControl';
import RoleChip from '../ui/RoleChip';
import { Avatar } from '../ui/Avatar';
import { X } from '../../lib/icons';

interface Props {
  thread: Thread;
  displayName: string;
  /** Opens the add-teammate picker (Phase 4). Button hidden until wired. */
  onOpenAddTeammate?: () => void;
  onClose: () => void;
}

const sectionStyle: React.CSSProperties = {
  padding: '16px 18px', borderBottom: '1px solid var(--border-subtle)',
};

// Houses everything evicted from the mobile header to fix its wrapping: Sign,
// participants, thread-type label, customer edit, leave. Reached via the
// header's "Thread ▾" button (mobile) or directly rendered inline on desktop
// (Phase 5).
export default function ThreadSheet({ thread, displayName, onOpenAddTeammate, onClose }: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const workspaceSignDefault = useChatStore((s) => s.workspaceSignDefault);
  const setSenderIdentity = useChatStore((s) => s.setSenderIdentity);
  const leaveThread = useChatStore((s) => s.leaveThread);
  const [editingContact, setEditingContact] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isClient = thread.type === 'client';
  const contact = isClient ? thread.contact : null;
  const showParticipants = thread.type !== 'direct';

  async function handleLeave() {
    setLeaving(true);
    setError(null);
    try {
      await leaveThread(thread.id);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to leave thread'));
      setLeaving(false);
    }
  }

  return (
    <Sheet title={displayName} onClose={onClose}>
      <div style={sectionStyle}>
        <span style={{
          fontSize: '0.68rem', fontWeight: 600, padding: '3px 8px', borderRadius: 2,
          ...badgeStyle[thread.type],
        }}>
          {typeLabel[thread.type]?.toUpperCase()}
        </span>
      </div>

      {isClient && (
        <div style={sectionStyle}>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
            {displayName} is on this thread over WhatsApp. Notes you post here are never delivered to them.
          </p>
        </div>
      )}

      {isClient && (
        <div style={sectionStyle}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--shell-xs)', marginBottom: 10 }}>
            SIGN OUTBOUND MESSAGES
          </div>
          <SegmentedControl
            fullWidth
            value={thread.sender_identity ?? 'default'}
            onChange={(v) => { setSenderIdentity(thread.id, v as SenderIdentity).catch(() => {}); }}
            options={[
              { value: 'default', label: 'Default' },
              { value: 'on', label: 'On' },
              { value: 'off', label: 'Off' },
            ]}
          />
          <p style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)', lineHeight: 1.5, marginTop: 10 }}>
            Appends your name to messages sent to the client, so they know which team member replied.{' '}
            <span style={{ color: 'var(--shell-muted)' }}>
              Default follows the workspace setting — currently {workspaceSignDefault ? 'on' : 'off'}.
            </span>
          </p>
        </div>
      )}

      {showParticipants && (
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--shell-xs)' }}>
              PARTICIPANTS · {thread.participants?.length ?? 0}
            </span>
            {isClient && onOpenAddTeammate && (
              <button
                onClick={onOpenAddTeammate}
                style={{ background: 'none', border: 'none', color: 'var(--v2)', fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Add person
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(thread.participants ?? []).map((p) => (
              <div key={p.tp_id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar name={p.name ?? p.contact_name} initialsOverride={p.avatar_initials} size={28} />
                <span style={{ flex: 1, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                  {p.name ?? p.contact_name}{p.user_id === currentUserId ? ' (you)' : ''}
                </span>
                <RoleChip role={participantRole(p)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div style={{ ...sectionStyle, color: 'var(--red)', fontSize: '0.78rem' }}>{error}</div>}

      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {contact && (
          <button
            onClick={() => setEditingContact(true)}
            style={{ background: 'none', border: 'none', textAlign: 'left', padding: '8px 0', color: 'var(--text-primary)', fontSize: '0.84rem', cursor: 'pointer' }}
          >
            Edit customer
          </button>
        )}
        {thread.type !== 'org' && (
          <button
            onClick={() => void handleLeave()}
            disabled={leaving}
            style={{ background: 'none', border: 'none', textAlign: 'left', padding: '8px 0', color: 'var(--red)', fontSize: '0.84rem', cursor: leaving ? 'wait' : 'pointer' }}
          >
            {leaving ? 'Leaving…' : 'Leave'}
          </button>
        )}
      </div>

      {editingContact && contact && (
        <EditContactDialog
          threadId={thread.id}
          contact={contact}
          onClose={() => setEditingContact(false)}
        />
      )}
    </Sheet>
  );
}

// ── Edit customer dialog ──────────────────────────────────────────────────────
// Lets staff override the WhatsApp-provided name and link the customer to a
// company. Leaving the name blank falls back to the WhatsApp name; leaving the
// company blank unlinks it. Moved here from ChatHeader.tsx (Phase 1).
function EditContactDialog({ threadId, contact, onClose }: {
  threadId: string;
  contact: ContactSummary;
  onClose: () => void;
}) {
  const updateContact = useChatStore((s) => s.updateContact);
  const [name, setName] = useState(contact.display_name ?? '');
  const [company, setCompany] = useState(contact.company ?? '');
  const [accounts, setAccounts] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
    clientAccountsApi.list()
      .then(({ data }) => setAccounts(data.map((a) => a.name)))
      .catch(() => {});
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await updateContact(threadId, contact.contact_id, {
        display_name: name.trim() || null,
        company: company.trim() || null,
      });
      onClose();
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--vs2)', border: '1px solid var(--vb)', borderRadius: 4,
    padding: '8px 10px', color: 'var(--text)', fontSize: '0.85rem', outline: 'none', width: '100%',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: '0.7rem', fontWeight: 600, color: 'var(--shell-xs)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 360, background: 'var(--vs1)',
          border: '1px solid var(--shell-border)', borderRadius: 8, padding: 18,
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--shell-text)' }}>Edit customer</span>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--shell-xs)',
            cursor: 'pointer', lineHeight: 1, padding: 2, display: 'flex',
          }}><X size={15} strokeWidth={1.8} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Display name</label>
          <input
            ref={firstFieldRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
            placeholder={contact.name}
            style={inputStyle}
          />
          <span style={{ fontSize: '0.68rem', color: 'var(--shell-xs)' }}>
            WhatsApp name: <b style={{ color: 'var(--shell-muted)' }}>{contact.name}</b>
            {name.trim() && (
              <button
                onClick={() => setName('')}
                style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--v2)', cursor: 'pointer', fontSize: '0.68rem', padding: 0 }}
              >
                reset
              </button>
            )}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Company</label>
          <input
            list="client-accounts-list"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
            placeholder="e.g. Acme Ltd"
            style={inputStyle}
          />
          <datalist id="client-accounts-list">
            {accounts.map((a) => <option key={a} value={a} />)}
          </datalist>
          <span style={{ fontSize: '0.68rem', color: 'var(--shell-xs)' }}>
            Links this customer (and their threads &amp; tasks) to a company. Pick an existing one or type a new name.
          </span>
        </div>

        {error && <div style={{ fontSize: '0.74rem', color: 'var(--red)' }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 14px', borderRadius: 4, background: 'transparent',
              border: '1px solid var(--vb)', color: 'var(--shell-muted)',
              fontSize: '0.8rem', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => void save()}
            disabled={saving}
            style={{
              padding: '7px 16px', borderRadius: 4, background: 'var(--v1)',
              border: 'none', color: 'var(--text-on-accent)', fontSize: '0.8rem', fontWeight: 700,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
